import React from 'react';
import { motion } from 'framer-motion';
import { systemModules, websiteNeeds } from '../../data/heroBlueprint';
const moduleBar = {
  orange: 'bg-forge-orange',
  cyan: 'bg-forge-cyan',
  green: 'bg-forge-green'
} as const;

/**
 * Hero object: many scattered business needs are absorbed into one custom website system.
 */
export function HeroBlueprint() {
  return <div className="relative" data-id="element-213" data-mp="ou7naou6ihry">
      <div className="pointer-events-none absolute -inset-6 rounded-[44px] bg-forge-cyan/[0.06] blur-3xl" aria-hidden="true" data-id="element-214" data-mp="awjb05e6giyi" />

      <div className="relative overflow-hidden rounded-[24px] border-2 border-white/14 bg-navy-950/90 shadow-lift" data-id="element-215" data-mp="fxgs0engb6yf">
        <div className="absolute inset-0 bp opacity-80" aria-hidden="true" data-id="element-216" data-mp="1xxndvtumdqv" />

        {/* title */}
        <div className="relative flex items-center gap-3 border-b border-white/10 px-6 py-5" data-id="element-217" data-mp="5xzl5twgqn0v">
          <span className="h-3 w-3 rotate-45 bg-forge-orange" aria-hidden="true" data-id="element-218" data-mp="1wax45s9ytdzq" />
          <h2 className="font-display text-[15px] font-extrabold leading-tight tracking-[-0.01em] text-white sm:text-[17px]" data-id="element-219" data-mp="20afnwkq1vj0z">
            One Website Built Around Your Business
          </h2>
        </div>

        <div className="relative grid items-center gap-0 px-5 py-6 sm:px-7 sm:py-8 lg:grid-cols-[0.74fr_auto_1.6fr]" data-id="element-220" data-mp="2zepkun8egtco">
          {/* ---------- needs ---------- */}
          <motion.div initial={{
          opacity: 0,
          x: -14
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: 0.08
        }} data-id="element-221" data-mp="394scok3sc19f">
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-forge-cyan" data-id="element-222" data-mp="2ggmr5v4wq88j">
              <span className="h-px w-4 bg-current opacity-60" aria-hidden="true" data-id="element-223" data-mp="2vdxcdw7cqshg" />
              Business needs
            </p>
            <ul className="mt-3.5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-1" data-id="element-224" data-mp="2qczkgrwjik2r">
              {websiteNeeds.map(need => <li key={need} className="rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-3 text-[13px] font-medium leading-snug text-mist-100" data-id="element-225" data-mp="ap987mjr5r5y">
                  {need}
                </li>)}
            </ul>
          </motion.div>

          <MergeFlow data-id="element-226" data-mp="mpz77hv30w9f" />

          {/* ---------- one custom website ---------- */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.55,
          delay: 0.22
        }} className="relative rounded-2xl border-2 border-forge-orange/45 bg-navy-900 p-5 shadow-[0_0_70px_-18px_rgba(255,107,24,0.6)] sm:p-6" data-id="element-227" data-mp="mnv77b0b0hef">
            <span className="absolute inset-x-0 top-0 h-[4px] rounded-t-2xl bg-forge-orange" aria-hidden="true" data-id="element-228" data-mp="3yomjify9fpz" />

            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-forge-orange" data-id="element-229" data-mp="8qzt34573zri">One custom website</p>
            <h3 className="mt-2 font-display text-[20px] font-extrabold leading-tight tracking-[-0.01em] text-white sm:text-[23px]" data-id="element-230" data-mp="37ff5exwmrn93">
              Built around your business
            </h3>

            <ul className="mt-5 grid gap-2.5 sm:grid-cols-2" data-id="element-231" data-mp="1tilzhkbq6a22">
              {systemModules.map((m, i) => <motion.li key={m.label} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.36 + i * 0.07
            }} className={`flex gap-3 rounded-xl bg-white/[0.055] px-4 py-3.5 ring-1 ring-inset ring-white/[0.09] ${i === systemModules.length - 1 ? 'sm:col-span-2' : ''}`} data-id="element-232" data-mp="1pitkopvqn7o9" data-mpi={i}>
                  <span className={`mt-0.5 h-8 w-[3px] shrink-0 rounded-full ${moduleBar[m.accent]}`} aria-hidden="true" data-id="element-233" data-mp="11rxz4ahvl8pg" />
                  <span data-id="element-234" data-mp="wuxu82wribzx">
                    <span className="block text-[14.5px] font-bold text-white" data-id="element-235" data-mp="wrjpio0mlqpb">{m.label}</span>
                    <span className="mt-0.5 block text-[12.5px] leading-snug text-mist-300" data-id="element-236" data-mp="rwnurg408p8i">{m.note}</span>
                  </span>
                </motion.li>)}
            </ul>
          </motion.div>
        </div>
      </div>
    </div>;
}

/** The needs converge into one route entering the website object. */
function MergeFlow() {
  return <div className="relative flex items-center justify-center py-4 lg:h-full lg:w-16 lg:py-0" aria-hidden="true" data-id="element-237" data-mp="2d8og7cafh75r">
      <svg viewBox="0 0 64 260" preserveAspectRatio="none" className="hidden h-full w-full lg:block" data-id="element-238" data-mp="2i5oo7squ8jzi">
        {[26, 84, 130, 176, 234].map(y => <path key={y} d={`M0 ${y} C 30 ${y}, 26 130, 58 130`} fill="none" stroke="#2BB6EE" strokeWidth="1.6" vectorEffect="non-scaling-stroke" opacity="0.5" className="flowline" data-id="element-239" data-mp="296bf516mlxfb" />)}
        <circle cx="59" cy="130" r="4" fill="#FF6B18" data-id="element-240" data-mp="3on224blmfcj5" />
      </svg>

      {/* stacked flow */}
      <div className="relative h-10 w-[3px] overflow-hidden rounded-full lg:hidden" style={{
      backgroundColor: '#2BB6EE33',
      boxShadow: '0 0 16px #2BB6EE55'
    }} data-id="element-241" data-mp="3tm6ax14mv88y">
        <motion.span className="absolute left-0 h-4 w-full rounded-full" style={{
        backgroundColor: '#2BB6EE',
        boxShadow: '0 0 12px #2BB6EE'
      }} animate={{
        y: ['-110%', '260%']
      }} transition={{
        duration: 1.8,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-242" data-mp="1hwqelodg4p9w" />
      </div>
    </div>;
}